/**
 * Inbox.jsx
 * Everything anybody said to you, in one place.
 *
 * This screen replaces four: Comments, Messages, Auto-reply and Approvals.
 * They were four sidebar entries answering one question — "who is waiting on
 * me?" — and splitting that question four ways is how a DM sat unanswered for
 * a week because somebody was looking at the Comments screen.
 *
 * Comments and messages are now one list. Auto-reply and Approvals are tabs
 * here rather than separate destinations, and they render the same components
 * they always did: those screens work, and rewriting them would be risk with
 * nothing bought.
 *
 * Replying still goes through the endpoints that own it, with the 24-hour
 * message window and the claim that stops two runs answering the same person
 * twice. Nothing on this screen is a second way to send.
 *
 * Dependencies: services/api.js, components/inbox/*, pages/AutoReply.jsx, pages/Reviews.jsx
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api.js';
import { useBrands } from '../hooks/useBrands.jsx';
import { Chat, Refresh, Sparkle, Warning } from '../components/Icons.jsx';
import NoBrands from '../components/NoBrands.jsx';

import ConversationList from '../components/inbox/ConversationList.jsx';
import Thread from '../components/inbox/Thread.jsx';
import ContactPanel from '../components/inbox/ContactPanel.jsx';
import TabBoundary from '../components/inbox/TabBoundary.jsx';
import { TABS, TOOL_TABS } from '../components/inbox/shared.js';

import AutoReply from './AutoReply.jsx';
import Reviews from './Reviews.jsx';

const PRIORITY_ORDER = { urgent: 0, high: 1, normal: 2, low: 3 };

export default function Inbox() {
  const { brands, loading: brandsLoading } = useBrands();

  /**
   * Which brand's inbox this is, or '' for every brand.
   *
   * Deliberately its own state rather than the app-wide selected brand. The
   * inbox answers "who is waiting on me", and the honest default for that is
   * everyone — a screen silently showing one brand's share of it is how a
   * whole brand's comments go unread. Every other screen still works as
   * before; this one has its own scope, and shows it.
   */
  const [scope, setScope] = useState('');

  const [tab, setTab] = useState('all');
  const [sort, setSort] = useState('newest');
  const [data, setData] = useState({ items: [], counts: {}, me: null });
  const [meta, setMeta] = useState({ stats: null, team: [], spend: {} });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);

  const isTool = TOOL_TABS.some((t) => t.key === tab);

  const load = useCallback(async () => {
    if (isTool) return;
    setLoading(true);
    try {
      const [list, stats] = await Promise.all([
        api.social.list({ brandId: scope || null, tab }),
        api.social.stats({ brandId: scope || null }),
      ]);
      setData(list);
      setMeta(stats);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [scope, tab, isTool]);

  useEffect(() => {
    load();
  }, [load]);

  /** Opens one conversation, which is also what creates its contact record. */
  const open = useCallback(async (item) => {
    const kind = item.kind;
    const refId = item.ref_id ?? item.refId;
    setSelected(`${kind}:${refId}`);
    setDetail(null);
    try {
      setDetail(await api.social.get(kind, refId));
    } catch (e) {
      setError(e);
    }
  }, []);

  // Reloading the list must not silently drop the open conversation.
  const refreshBoth = useCallback(async () => {
    await load();
    if (selected) {
      const [kind, refId] = selected.split(':');
      try {
        setDetail(await api.social.get(kind, refId));
      } catch {
        setDetail(null);
      }
    }
  }, [load, selected]);

  /** Pulls from Meta. Free; it is the AI button next to it that spends. */
  async function sync() {
    setBusy(true);
    setNote(null);
    try {
      const [comments, messages] = await Promise.all([
        api.inbox.refresh(),
        api.inbox.refreshMessages(),
      ]);
      setNote(
        `Checked ${comments.checked ?? 0} posts and ${messages.checked ?? messages.conversations ?? 0} conversations.`,
      );
      await refreshBoth();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Judges what is on screen and not yet judged.
   *
   * Deliberately only the unjudged: re-running over the same forty comments
   * would be a bill for no new information.
   */
  async function triage() {
    const pending = data.items.filter((i) => !i.triaged_at).slice(0, meta.triageBatch ?? 20);
    if (pending.length === 0) {
      setNote('Everything on screen has already been judged.');
      return;
    }
    if (!scope) {
      setError(
        new Error(
          'Choose a single brand before triaging. The judgement is made in that brand’s voice and against its banned claims, so it cannot be run across all of them at once.',
        ),
      );
      return;
    }

    setBusy(true);
    setNote(null);
    try {
      const res = await api.social.triage({
        brandId: scope,
        items: pending.map((i) => ({ kind: i.kind, ref_id: i.ref_id, body: i.body })),
      });
      setNote(res.message);
      await refreshBoth();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  /** Opening and closing a conversation. Local only — the platform sees none of it. */
  async function resolve() {
    if (!detail) return;
    const { kind, refId } = detail.item;
    setBusy(true);
    try {
      await api.social.setState(kind, refId, {
        status: detail.state?.status === 'resolved' ? 'open' : 'resolved',
      });
      await refreshBoth();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  // Sorting is local: the list is already here, and a round trip to reorder
  // twenty rows would be slower than the browser doing it.
  const items = useMemo(() => {
    const list = [...(data.items ?? [])];
    if (sort === 'oldest') return list.reverse();
    if (sort === 'priority') {
      return list.sort(
        (a, b) => (PRIORITY_ORDER[a.priority] ?? 2) - (PRIORITY_ORDER[b.priority] ?? 2),
      );
    }
    return list;
  }, [data.items, sort]);

  if (!brandsLoading && brands.length === 0) return <NoBrands />;

  const stats = meta.stats;

  return (
    <>
      <div className="page-head page-head--hero">
        <div>
          <div className="page-head__eyebrow">Respond</div>
          <h1>
            <span className="copilot-mark">
              <Chat size={26} />
            </span>
            Social inbox
          </h1>
          <p>
            Comments, direct messages and everything your team knows about the people behind
            them — in one place.
          </p>
        </div>

        <div className="page-head__actions">
          <select
            className="select"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            aria-label="Which brand"
          >
            <option value="">All brands</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>

          <button className="btn" onClick={sync} disabled={busy}>
            <Refresh size={16} /> {busy ? 'Working…' : 'Refresh'}
          </button>
          <button
            className="btn btn--primary"
            onClick={triage}
            disabled={busy || isTool}
            title={
              scope
                ? 'Judges sentiment, intent and purchase intent for the conversations on screen. Counts towards your monthly AI spend.'
                : 'Choose a single brand first — a judgement is made in one brand’s voice.'
            }
          >
            <Sparkle size={16} /> Triage with AI
          </button>
        </div>
      </div>

      {stats && <StatRow stats={stats} />}

      <div className="tab-row tab-row--scroll" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            type="button"
            aria-selected={tab === t.key}
            className={`tab${tab === t.key ? ' is-active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {data.counts?.[t.count] > 0 && <span className="tab__count">{data.counts[t.count]}</span>}
          </button>
        ))}

        <span className="tab-row__spacer" />

        {/* The two screens that used to live in the sidebar. */}
        {TOOL_TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            type="button"
            aria-selected={tab === t.key}
            className={`tab${tab === t.key ? ' is-active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            <t.Icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {note && (
        <div className="notice notice--info">
          <Sparkle />
          <span>{note}</span>
        </div>
      )}

      {/* These two are whole screens rendered inside a tab. A crash in either
          used to cost one page; it now costs the screen people answer
          customers on, so each is wrapped. */}
      {tab === 'autoreply' && (
        <TabBoundary name="Auto-reply">
          <AutoReply />
        </TabBoundary>
      )}
      {tab === 'approvals' && (
        <TabBoundary name="Approvals">
          <Reviews />
        </TabBoundary>
      )}

      {!isTool && (
        <div className="inbox-grid">
          <ConversationList
            items={items}
            selected={selected}
            onSelect={open}
            loading={loading}
            unavailable={data.unavailable}
            elsewhere={scope ? (data.everywhere?.all ?? 0) - (data.counts?.all ?? 0) : 0}
            onShowAll={() => setScope('')}
            sort={sort}
            onSort={setSort}
          />

          {detail ? (
            <>
              <Thread
                detail={detail}
                onChanged={refreshBoth}
                onError={setError}
                onResolve={resolve}
                busy={busy}
              />
              <ContactPanel
                detail={detail}
                team={meta.team}
                onChanged={refreshBoth}
                onError={setError}
                onOpen={open}
              />
            </>
          ) : (
            <section className="card inbox-empty">
              <div className="empty empty--inline">
                <div className="empty__art">
                  <Chat size={26} />
                </div>
                <h3>Nothing selected</h3>
                <p>
                  Choose a conversation to read it, reply, leave a note for your team, or see what
                  this person has said before.
                </p>
              </div>
            </section>
          )}
        </div>
      )}
    </>
  );
}

// ============================================
// THE FIGURES
// ============================================

/**
 * Four numbers, each honest about what it is.
 *
 * Response rate and resolved-today are counted. Leads and negative sentiment
 * are only as complete as the triage behind them, so the card says how much of
 * the window has actually been judged rather than letting "3 negative" read as
 * the whole truth.
 */
function StatRow({ stats }) {
  const judged = stats.triaged;
  const partial = judged < stats.total;

  const cells = [
    {
      label: 'Response rate',
      value: stats.responseRate === null ? '—' : `${stats.responseRate.toFixed(0)}%`,
      note:
        stats.responseRate === null
          ? 'Nothing in the last 30 days'
          : `${stats.answered} of ${stats.total} answered`,
      tone: 'ok',
    },
    {
      label: 'Resolved today',
      value: String(stats.resolvedToday),
      note: `${stats.openNow} still open`,
      tone: 'accent',
    },
    {
      label: 'Potential leads',
      value: String(stats.leads),
      note: partial ? `of ${judged} judged so far` : 'across the last 30 days',
      tone: 'ok',
      judged: true,
    },
    {
      label: 'Negative sentiment',
      value: String(stats.negative),
      note: partial ? `of ${judged} judged so far` : 'across the last 30 days',
      tone: 'danger',
      judged: true,
    },
  ];

  return (
    <div className="stat-row stat-row--inbox">
      {cells.map((c) => (
        <div className={`stat stat--flat stat--${c.tone}`} key={c.label}>
          <span className="stat__label">
            {c.label}
            {c.judged && (
              <span className="stat__ai" title="Counted from AI judgements, not measurements">
                <Sparkle size={11} />
              </span>
            )}
          </span>
          <span className="stat__value">{c.value}</span>
          <span className="stat__note">{c.note}</span>
        </div>
      ))}
    </div>
  );
}
